<script setup>
import { useAlert } from 'dashboard/composables';
import AceleresCreateCannedModal from 'dashboard/components/widgets/WootWriter/AceleresCreateCannedModal.vue';
import SettingsLayout from '../SettingsLayout.vue';
import {
  computed,
  onActivated,
  onDeactivated,
  onMounted,
  ref,
  defineOptions,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import {
  SIDEBAR_PAGE_IDS,
  buildCannedDefaultSidebarPageKey,
  isSidebarPageReady,
  markSidebarPageReady,
} from 'dashboard/helper/sidebarPagePreload';

defineOptions({
  name: 'CannedResponseSettings',
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const { getPlainText } = useMessageFormatter();

// Mesmo gate de agents/Index.vue e teams/Index.vue: com <keep-alive> o header
// teleportado sobreviveria a saida da rota e apareceria na pagina seguinte.
const isPageActive = ref(true);
onActivated(() => {
  isPageActive.value = true;
});
onDeactivated(() => {
  isPageActive.value = false;
});

const showAddPopup = ref(false);
const loading = ref({});
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const activeResponse = ref({});
const cannedResponseAPI = ref({ message: '' });

const sortOrder = ref('asc');
const searchQuery = ref('');
const attachmentFilter = ref('all');
const recordHasAttachment = record =>
  (Array.isArray(record?.attachments) && record.attachments.length > 0) ||
  Boolean(record?.attachment_url);

const records = computed(() =>
  getters.getSortedCannedResponses.value(sortOrder.value)
);

const attachmentCount = computed(
  () => records.value.filter(record => recordHasAttachment(record)).length
);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  const recordsForFilter =
    attachmentFilter.value === 'attachments'
      ? records.value.filter(record => recordHasAttachment(record))
      : records.value;

  if (!query) return recordsForFilter;
  return picoSearch(recordsForFilter, query, [
    { name: 'short_code', weight: 4 },
    'content',
  ]);
});
const uiFlags = computed(() => getters.getUIFlags.value);

const deleteConfirmText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.YES')} ${activeResponse.value.short_code}`
);

const deleteRejectText = computed(
  () =>
    `${t('CANNED_MGMT.DELETE.CONFIRM.NO')} ${activeResponse.value.short_code}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.short_code} ? `;
});

// O atalho é digitado com a barra na conversa; a lista mostra e copia do mesmo
// jeito que o agente vai usar.
const shortCodeWithSlash = shortCode => {
  const code = String(shortCode ?? '').trim();
  return code.startsWith('/') ? code : `/${code}`;
};

const messageText = content =>
  getPlainText(content || '')
    .replace(/\s+/g, ' ')
    .trim();

const recordAttachmentCount = record => {
  if (Array.isArray(record?.attachments) && record.attachments.length) {
    return record.attachments.length;
  }
  return record?.attachment_url ? 1 : 0;
};

const toggleSort = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
};

const setAttachmentFilter = filter => {
  attachmentFilter.value = filter;
};

const fetchCannedResponses = async () => {
  try {
    await store.dispatch('getCannedResponse');
    markSidebarPageReady(
      SIDEBAR_PAGE_IDS.CANNED_DEFAULT,
      buildCannedDefaultSidebarPageKey()
    );
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  if (
    isSidebarPageReady(
      SIDEBAR_PAGE_IDS.CANNED_DEFAULT,
      buildCannedDefaultSidebarPageKey()
    )
  ) {
    return;
  }
  fetchCannedResponses();
});

const showAlertMessage = message => {
  loading[activeResponse.value.id] = false;
  activeResponse.value = {};
  cannedResponseAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  activeResponse.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const copyShortCode = async shortCode => {
  try {
    await copyTextToClipboard(shortCodeWithSlash(shortCode));
    useAlert(t('CANNED_MGMT.COPY.SUCCESS'));
  } catch (error) {
    useAlert(t('CANNED_MGMT.COPY.ERROR'));
  }
};

const deleteCannedResponse = async id => {
  try {
    await store.dispatch('deleteCannedResponse', id);
    showAlertMessage(t('CANNED_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CANNED_MGMT.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCannedResponse(activeResponse.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('CANNED_MGMT.LIST.TABLE_HEADER.SHORT_CODE'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.CONTENT'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.ATTACHMENTS'),
    t('CANNED_MGMT.LIST.TABLE_HEADER.ACTIONS'),
  ];
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('CANNED_MGMT.LOADING')"
  >
    <template #header>
      <Teleport
        v-if="isPageActive"
        defer
        to=".settings-wrapper__teams-header-outlet"
      >
        <header
          class="canned-page__header acl-mobile-account-menu-host flex flex-shrink-0 items-center justify-between gap-4 px-0 py-4 transition-[padding] duration-200 ease-out max-md:-mx-10 max-md:gap-2 max-md:px-4 max-md:py-3"
        >
          <div class="flex min-w-0 items-center gap-4 max-md:flex-1">
            <div class="canned-page__header-content min-w-0 max-md:w-full">
              <div
                class="flex min-w-0 items-center gap-3 max-md:gap-2"
              >
                <h1
                  class="canned-page__title truncate text-[26px] font-bold leading-8 text-n-slate-12 max-md:flex-1 max-md:!overflow-visible max-md:!whitespace-normal max-md:!text-clip max-md:text-left max-md:text-xl max-md:leading-6"
                >
                  {{ $t('CANNED_MGMT.HEADER') }}
                </h1>
                <span
                  v-if="records.length"
                  class="inline-flex h-6 min-w-7 shrink-0 items-center justify-center rounded-full bg-n-solid-1 px-2 text-xs font-semibold text-n-slate-11"
                >
                  {{ records.length }}
                </span>
              </div>
              <p
                class="canned-page__subtitle mb-0 mt-1 text-sm text-n-slate-11 max-md:text-left max-md:text-xs max-md:leading-4"
              >
                {{ $t('CANNED_MGMT.PAGE_SUBTITLE') }}
              </p>
            </div>
          </div>
          <Button
            :label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
            icon="i-lucide-plus"
            size="md"
            class="shrink-0 max-md:hidden"
            @click="openAddPopup"
          />
          <div class="hidden shrink-0 max-md:block">
            <Button
              icon="i-lucide-plus"
              size="md"
              :aria-label="$t('CANNED_MGMT.HEADER_BTN_TXT')"
              @click="openAddPopup"
            />
          </div>
        </header>
      </Teleport>
    </template>

    <template #body>
      <main class="flex min-h-0 w-full flex-1 flex-col gap-4 max-md:gap-3">
        <div
          class="flex min-h-12 flex-wrap items-center justify-between gap-x-6 gap-y-2 rounded-xl bg-n-solid-1 px-4 py-3 text-sm text-n-slate-11 max-md:items-start max-md:px-3 max-md:py-2 max-md:text-xs"
        >
          <div class="flex flex-wrap items-center gap-3 max-md:gap-x-2 max-md:gap-y-1">
            <span
              class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
            >
              {{ $t('CANNED_MGMT.HOW_TO.LABEL') }}
            </span>
            <span class="hidden h-5 border-l border-n-weak sm:block" />
            <span>{{ $t('CANNED_MGMT.HOW_TO.TYPE_IN_CHAT') }}</span>
            <kbd
              class="rounded-md border border-n-weak px-2 py-0.5 font-mono text-xs text-n-slate-12"
              >{{ $t('CANNED_MGMT.HOW_TO.SLASH') }}</kbd
            >
            <span>{{ $t('CANNED_MGMT.HOW_TO.PLUS_SHORTCUT') }}</span>
            <code
              class="rounded-md bg-n-brand/10 px-2 py-1 font-mono text-xs text-n-blue-11"
              >{{ $t('CANNED_MGMT.HOW_TO.EXAMPLE_SHORTCUT') }}</code
            >
            <span>{{ $t('CANNED_MGMT.HOW_TO.AND_PRESS') }}</span>
            <kbd
              class="rounded-md border border-n-weak px-2 py-0.5 font-mono text-xs text-n-slate-12"
              >{{ $t('CANNED_MGMT.HOW_TO.ENTER_KEY') }}</kbd
            >
          </div>
          <span class="ml-auto text-xs text-n-slate-10 max-md:hidden">
            {{ $t('CANNED_MGMT.HOW_TO.TIP') }}
          </span>
        </div>

        <div
          class="flex min-h-[30rem] max-md:min-h-0 w-full flex-1 flex-col overflow-hidden rounded-2xl bg-n-solid-1 shadow-sm outline outline-1 outline-n-weak"
        >
          <div
            class="flex flex-wrap items-center justify-between gap-4 border-b border-n-weak px-5 py-4 max-md:flex-col max-md:items-stretch max-md:gap-2 max-md:px-3 max-md:py-3"
          >
            <Input
              v-model="searchQuery"
              :placeholder="$t('CANNED_MGMT.SEARCH_PLACEHOLDER')"
              class="group w-full min-w-0 sm:w-96 [&>input]:ltr:!pl-9 [&>input]:rtl:!pr-9 [&>input]:!rounded-[0.625rem] [&>input]:!h-10 [&>input]:!bg-n-surface-2"
              size="sm"
              type="search"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-4 ltr:left-3 rtl:right-3"
                />
              </template>
            </Input>

            <div class="flex items-center gap-2 max-md:w-full max-md:flex-wrap">
              <button
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm transition-colors"
                :aria-pressed="attachmentFilter === 'all'"
                :class="
                  attachmentFilter === 'all'
                    ? 'bg-n-slate-12 font-semibold text-white'
                    : 'bg-n-surface-2 text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="setAttachmentFilter('all')"
              >
                {{ $t('CANNED_MGMT.FILTER.ALL') }}
                <span>{{ records.length }}</span>
              </button>
              <button
                type="button"
                class="inline-flex h-9 items-center gap-2 rounded-full px-3 text-sm transition-colors"
                :aria-pressed="attachmentFilter === 'attachments'"
                :class="
                  attachmentFilter === 'attachments'
                    ? 'bg-n-slate-12 font-semibold text-white'
                    : 'bg-n-surface-2 text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="setAttachmentFilter('attachments')"
              >
                {{ $t('CANNED_MGMT.FILTER.WITH_ATTACHMENT') }}
                <span>{{ attachmentCount }}</span>
              </button>
              <button
                type="button"
                class="inline-flex h-9 items-center gap-1 rounded-full border border-n-weak px-3 text-sm text-n-slate-11 hover:bg-n-alpha-1"
                :aria-label="$t('CANNED_MGMT.SORT_LABEL')"
                @click="toggleSort"
              >
                {{ sortOrder === 'asc' ? 'A–Z' : 'Z–A' }}
                <Icon icon="i-lucide-chevron-down" class="size-3.5" />
              </button>
            </div>
          </div>

          <div
            v-if="records.length"
            class="min-h-0 flex-1 overflow-x-auto [&_th:first-child]:ltr:pl-7 [&_th:first-child]:rtl:pr-7 [&_td:first-child]:ltr:pl-7 [&_td:first-child]:rtl:pr-7 [&_th:last-child]:ltr:pr-7 [&_th:last-child]:rtl:pl-7 [&_td:last-child]:ltr:pr-7 [&_td:last-child]:rtl:pl-7"
          >
            <BaseTable
              v-if="filteredRecords.length"
              :headers="tableHeaders"
              :items="filteredRecords"
              class="[&_th]:text-xs [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-n-slate-10 max-md:[&_table]:block max-md:[&_thead]:hidden max-md:[&_tbody]:block max-md:[&_tbody]:divide-y-0"
            >
              <template #header-0>
                {{ tableHeaders[0] }}
              </template>
              <template #header-1>
                {{ tableHeaders[1] }}
              </template>
              <template #header-2>
                {{ tableHeaders[2] }}
              </template>
              <template #header-3>
                <span class="block text-end">{{ tableHeaders[3] }}</span>
              </template>

              <template #row="{ items }">
                <BaseTableRow
                  v-for="cannedItem in items"
                  :key="cannedItem.short_code"
                  :item="cannedItem"
                  class="group hover:bg-n-alpha-1 max-md:mx-3 max-md:mb-3 max-md:block max-md:rounded-xl max-md:border max-md:border-n-weak max-md:bg-n-solid-1 max-md:px-4 max-md:py-3"
                >
                  <template #default>
                    <BaseTableCell
                      class="w-px whitespace-nowrap align-middle max-md:block max-md:w-full max-md:py-0 max-md:!px-0"
                    >
                      <div
                        class="flex items-center gap-2 max-md:justify-between"
                      >
                        <span
                          class="inline-flex items-center h-8 px-3 rounded-lg bg-n-brand/10 text-n-blue-11 text-heading-3"
                        >
                          {{ shortCodeWithSlash(cannedItem.short_code) }}
                        </span>
                        <Button
                          v-tooltip.top="$t('CANNED_MGMT.COPY.TOOLTIP')"
                          icon="i-lucide-copy"
                          ghost
                          slate
                          sm
                          class="md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
                          @click="copyShortCode(cannedItem.short_code)"
                        />
                      </div>
                    </BaseTableCell>

                    <BaseTableCell
                      class="max-w-0 align-middle max-md:block max-md:w-full max-md:max-w-none max-md:py-2 max-md:!px-0"
                    >
                      <div class="flex flex-col gap-1 min-w-0 max-md:w-full">
                        <span
                          class="flex items-center gap-1 text-heading-3 text-n-slate-12 truncate max-md:block max-md:whitespace-normal max-md:break-words max-md:line-clamp-3"
                        >
                          {{ messageText(cannedItem.content) }}
                        </span>
                      </div>
                    </BaseTableCell>

                    <BaseTableCell
                      class="w-40 align-middle max-md:block max-md:w-full max-md:py-0 max-md:!px-0"
                    >
                      <span
                        v-if="recordAttachmentCount(cannedItem)"
                        class="inline-flex items-center gap-1.5 rounded-full bg-n-surface-2 px-2.5 py-1 text-xs text-n-slate-11"
                      >
                        <Icon icon="i-lucide-paperclip" class="size-3.5" />
                        {{
                          $t('CANNED_MGMT.ATTACHMENT.COUNT', {
                            n: recordAttachmentCount(cannedItem),
                          })
                        }}
                      </span>
                      <span v-else class="text-n-slate-9 max-md:hidden">—</span>
                    </BaseTableCell>

                    <BaseTableCell
                      align="end"
                      class="w-24 align-middle max-md:block max-md:w-full max-md:border-t max-md:border-n-weak max-md:mt-2 max-md:pt-2 max-md:pb-0 max-md:!px-0"
                    >
                      <div
                        class="hidden gap-3 justify-end flex-shrink-0 md:flex"
                      >
                        <Button
                          v-tooltip.top="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                          icon="i-woot-edit-pen"
                          slate
                          sm
                          @click="openEditPopup(cannedItem)"
                        />
                        <Button
                          v-tooltip.top="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                          icon="i-woot-bin"
                          slate
                          sm
                          class="!text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                          :is-loading="loading[cannedItem.id]"
                          @click="openDeletePopup(cannedItem)"
                        />
                      </div>
                      <div class="grid grid-cols-2 gap-2 md:hidden">
                        <Button
                          :label="$t('CANNED_MGMT.EDIT.BUTTON_TEXT')"
                          icon="i-woot-edit-pen"
                          variant="outline"
                          color="slate"
                          size="md"
                          class="!h-11 w-full"
                          @click="openEditPopup(cannedItem)"
                        />
                        <Button
                          :label="$t('CANNED_MGMT.DELETE.BUTTON_TEXT')"
                          icon="i-woot-bin"
                          variant="faded"
                          color="ruby"
                          size="md"
                          class="!h-11 w-full"
                          :is-loading="loading[cannedItem.id]"
                          @click="openDeletePopup(cannedItem)"
                        />
                      </div>
                    </BaseTableCell>
                  </template>
                </BaseTableRow>
              </template>
            </BaseTable>

            <div
              v-else
              class="px-4 py-20 text-center border-t border-n-weak text-body-main !text-base text-n-slate-11"
            >
              {{ $t('CANNED_MGMT.NO_RESULTS') }}
            </div>
          </div>

          <div
            v-else
            class="flex flex-col items-center justify-center gap-2 px-4 py-14 border-t border-n-weak"
          >
            <svg
              viewBox="0 0 132 100"
              fill="none"
              aria-hidden="true"
              class="w-32 h-24 text-n-brand"
            >
              <circle
                cx="82"
                cy="56"
                r="26"
                fill="currentColor"
                fill-opacity="0.08"
              />
              <path
                d="M34 20h48a12 12 0 0 1 12 12v22a12 12 0 0 1-12 12H60L44 80V66h-10a12 12 0 0 1-12-12V32a12 12 0 0 1 12-12Z"
                fill="currentColor"
                fill-opacity="0.06"
                stroke="currentColor"
                stroke-opacity="0.5"
                stroke-width="3"
                stroke-linejoin="round"
              />
              <circle
                cx="46"
                cy="43"
                r="3.4"
                fill="currentColor"
                fill-opacity="0.45"
              />
              <circle
                cx="58"
                cy="43"
                r="3.4"
                fill="currentColor"
                fill-opacity="0.45"
              />
              <circle
                cx="70"
                cy="43"
                r="3.4"
                fill="currentColor"
                fill-opacity="0.45"
              />
              <path
                d="M105 14c1 5.6 2.4 7 8 8-5.6 1-7 2.4-8 8-1-5.6-2.4-7-8-8 5.6-1 7-2.4 8-8Z"
                fill="currentColor"
                fill-opacity="0.35"
              />
              <path
                d="M20 44c.8 4.4 1.9 5.5 6.3 6.3-4.4.8-5.5 1.9-6.3 6.3-.8-4.4-1.9-5.5-6.3-6.3 4.4-.8 5.5-1.9 6.3-6.3Z"
                fill="currentColor"
                fill-opacity="0.3"
              />
              <path
                d="M112 56c.7 3.7 1.6 4.6 5.3 5.3-3.7.7-4.6 1.6-5.3 5.3-.7-3.7-1.6-4.6-5.3-5.3 3.7-.7 4.6-1.6 5.3-5.3Z"
                fill="currentColor"
                fill-opacity="0.3"
              />
              <circle
                cx="36"
                cy="86"
                r="2.6"
                fill="currentColor"
                fill-opacity="0.25"
              />
              <circle
                cx="98"
                cy="30"
                r="2"
                fill="currentColor"
                fill-opacity="0.25"
              />
            </svg>

            <h3 class="mb-0 text-heading-2 text-n-slate-12">
              {{ $t('CANNED_MGMT.EMPTY_STATE.TITLE') }}
            </h3>
            <p class="mb-0 text-body-main text-n-slate-11 text-center">
              {{ $t('CANNED_MGMT.EMPTY_STATE.SUBTITLE') }}
            </p>
            <Button
              :label="$t('CANNED_MGMT.EMPTY_STATE.BUTTON')"
              variant="outline"
              size="md"
              class="mt-3"
              @click="openAddPopup"
            />
          </div>

          <div
            v-if="records.length"
            class="mt-auto border-t border-n-weak px-7 py-3 text-sm text-n-slate-11"
          >
            {{
              $t('CANNED_MGMT.FOOTER.SHOWING', { n: filteredRecords.length })
            }}
          </div>
        </div>
      </main>
    </template>

    <AceleresCreateCannedModal
      :show="showAddPopup"
      @close="hideAddPopup"
      @saved="fetchCannedResponses"
    />

    <AceleresCreateCannedModal
      :show="showEditPopup"
      :editing="activeResponse"
      @close="hideEditPopup"
      @saved="fetchCannedResponses"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CANNED_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('CANNED_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      modal-container-class="canned-page__delete-modal max-md:mx-4 max-md:max-h-[calc(100dvh-2rem)] max-md:w-[calc(100%-2rem)] max-md:rounded-2xl"
      header-class="canned-page__delete-modal-header max-md:px-5 max-md:pb-0 max-md:pr-12 max-md:pt-6"
      actions-class="canned-page__delete-modal-actions max-md:flex-col-reverse max-md:items-stretch max-md:px-5 max-md:pb-5 max-md:pt-6 max-md:[&>button]:h-auto max-md:[&>button]:min-h-11 max-md:[&>button]:w-full max-md:[&>button]:whitespace-normal"
    />
  </SettingsLayout>
</template>
